import React from 'react';
import { Play, Circle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TemplateLightEpisodeCardProps {
  index: number;
  title: string;
  duration?: string;
  thumbnail?: string;
  isPlaying: boolean;
  isWatched: boolean;
  progress?: number;
  onClick: () => void;
}

export function TemplateLightEpisodeCard({
  index,
  title,
  duration,
  thumbnail,
  isPlaying,
  isWatched,
  progress,
  onClick,
}: TemplateLightEpisodeCardProps) {
  const getStatusIcon = () => {
    if (isPlaying) return <Play className="w-3 h-3 fill-current" />;
    if (isWatched) return <Check className="w-3 h-3" />;
    return <Circle className="w-3 h-3" />;
  };

  const getStatusColor = () => {
    if (isPlaying) return 'text-[#5A8CFF]';
    if (isWatched) return 'text-green-600';
    return 'text-gray-400';
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left flex items-center gap-3.5 py-3.5 px-4 rounded-lg',
        'transition-all duration-200 ease-out',
        'hover:bg-white hover:scale-[1.02] hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)]',
        isPlaying
          ? 'bg-[rgba(79,102,255,0.06)] border-l-[3px] border-l-[#4F66FF] shadow-[0_2px_8px_rgba(79,102,255,0.12)]'
          : 'border-l-[3px] border-l-transparent'
      )}
    >
      {/* Thumbnail com Progress Bar */}
      <div className="w-[110px] h-[65px] bg-gray-200 rounded-lg overflow-hidden flex-shrink-0 relative">
        {thumbnail ? (
          <img 
            src={thumbnail} 
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400">
            <Play className="w-6 h-6 text-white/70" />
          </div>
        )}
        
        {/* Barra de Progresso Visual */}
        {progress !== undefined && progress > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-black/20">
            <div 
              className="h-full bg-[#3B82F6] transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        )}
        
        {/* Badge de 100% concluído */}
        {isWatched && (
          <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
            <Check className="w-2.5 h-2.5 text-white" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h4 className="text-[15px] font-medium text-[#1A1F36] line-clamp-2 mb-1">
          {title}
        </h4>
        
        {/* Duração abaixo do nome */}
        {duration && (
          <span className="text-[13px] text-[#6B7280] font-normal">
            {duration}
          </span>
        )}
      </div>
    </button>
  );
}
